import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import { Tabs, Tab, Button, Form, Row } from 'react-bootstrap';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons';
import {
  RtbCampaignSetupStepProps,
  CampaignSetupTab
} from './RtbCampaignSetupStepModel';
import styles from './rtbCampaignSetupStep.module.scss';
import { EditLimitation } from 'containers/Limitations/EditLimitation';
import { WeatherLimitationSection } from 'containers/Limitations/WeatherLimitationSection';
import CampaignBasicForm from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicForm';
import CampaignTrackForm from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignTrackForm';
import {
  RTBCAMPAIGN_DEFAULT_AGE_MAX,
  RTBCAMPAIGN_DEFAULT_AGE_MIN
} from 'core/rtbCampaign/RtbCampaign';
import { Formik } from 'formik';
import i18n from 'i18next';
import Select from 'components/common/Select/Select';
import {
  ageMaxOptions,
  ageMinOptions,
  getGenderOptions,
  getPmax2RtbAgeGroupsByAgeRange
} from 'core/limitation/l2ObjectTAOptions';
import { defaultTo, get, partial, remove } from 'lodash';
import { FinalReportGender, Order } from 'core/order/Order';
import {
  AbstractOutdoorLimitationCampaignFormModel,
  AbstractOutdoorSynchronizedPlaybackFormModel
} from '../RtbCampaignForm/OutdoorLimitationCampaignFormModel';
import { AbstractDynamicMovableOutdoorFormModel } from '../RtbCampaignForm/DynamicMovableOutdoorFormModel';
import { MovableOutdoorGeocoderBinder } from '../RtbCampaignForm/DynamicMovableOutdoorProviders';
import { FormikField } from 'components/common/form/field/FormikField';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { OPERATE } from 'enum/Operate';
import { ScreenDisplayType } from 'core/channelGroup/ChannelGroup';
import { OutdoorMapSection } from 'components/CampaignLimitation/CustomOutdoorDevice/OutdoorMapSection';
import { FileInputWithFieldLayout } from 'components/common/form/field/FileInputField';
import { Trans } from 'react-i18next';
import { formatBytes } from 'utils/StringUtil';
import config from 'config';

const IMPORT_OUTDOOR_DEVICE_MAX_SIZE = 1024 * 1024;

type State = {
  importError?: string;
};

export class RtbCampaignSetupStep extends React.Component<
  RtbCampaignSetupStepProps,
  State
> {
  state: State = {};

  componentDidUpdate() {
    this.scrollToErrorField();
  }

  componentWillUnmount() {
    this.props.model.onUnmount();
  }

  handleSubmit = async (_1) => {
    /* no-op */
  };

  validate = (campaign) => {
    return this.props.model.validateCampaignBasic(campaign);
  };

  scrollToErrorField() {
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(this);
    if (node instanceof HTMLElement) {
      const child = node.querySelector('.errorTip');
      child &&
        child.scrollIntoView({
          block: 'center'
        });
    }
  }

  getOptValueOfTA = (includeTA) => {
    return includeTA ? 'include' : 'preferred';
  };

  renderAgeAndGender = (
    limitationValueSet,
    order: Order,
    onChange: (operate: string, type: string, value: any) => void
  ) => {
    const generalTAInclude = defaultTo(limitationValueSet['include'], []);
    const generalTAPreferred = defaultTo(limitationValueSet['preferred'], []);
    const ageMinInclude = generalTAInclude.find(
      (value) => value.type === 'age_min'
    );
    const ageMinPreferred = generalTAPreferred.find(
      (value) => value.type === 'age_min'
    );
    const ageMaxInclude = generalTAInclude.find(
      (value) => value.type === 'age_max'
    );
    const ageMaxPreferred = generalTAPreferred.find(
      (value) => value.type === 'age_max'
    );
    const genderInclude = generalTAInclude.find(
      (value) => value.type === 'gender'
    );
    const genderPerferred = generalTAPreferred.find(
      (value) => value.type === 'gender'
    );

    const ageOptValue = this.getOptValueOfTA(ageMinInclude);
    const genderOptValue = this.getOptValueOfTA(genderInclude);

    const ageMinLimitation = ageMinInclude || ageMinPreferred;
    const ageMin = get(ageMinLimitation, 'value', RTBCAMPAIGN_DEFAULT_AGE_MIN);

    const ageMaxLimitation = ageMaxInclude || ageMaxPreferred;
    const ageMax = get(ageMaxLimitation, 'value', RTBCAMPAIGN_DEFAULT_AGE_MAX);

    const gendersLimitation = genderInclude || genderPerferred;
    const gender = get(
      gendersLimitation,
      'value[0]',
      getGenderOptions().find(
        (option) => option.value === FinalReportGender.ALL
      )
    );

    const onAgeMinChange = (newAgeMin) => {
      if (newAgeMin > ageMax) {
        onChange(ageOptValue, 'age_max', newAgeMin);
      }
      onChange(ageOptValue, 'age_min', newAgeMin);
    };
    const onAgeMaxChange = (newAgeMax) => {
      if (newAgeMax < ageMin) {
        onChange(ageOptValue, 'age_min', newAgeMax);
      }
      onChange(ageOptValue, 'age_max', newAgeMax);
    };
    const onAgeOptChange = (e) => {
      const opts = ['preferred', 'include'];
      const optsToSetValue = remove(opts, (opt) => opt === e.target.value);
      const optToSetValue = defaultTo(optsToSetValue[0], 'preferred');
      const optToSetUndefined = defaultTo(opts[0], 'include');
      onChange(optToSetValue, 'age_min', ageMin);
      onChange(optToSetValue, 'age_max', ageMax);
      onChange(optToSetUndefined, 'age_min', undefined);
      onChange(optToSetUndefined, 'age_max', undefined);
    };

    const getGenderValue = (gender) =>
      gender.value === -1 ? undefined : [gender];
    const onGendersChange = (gender) => {
      onChange(genderOptValue, 'gender', getGenderValue(gender));
    };
    const onGenderOptChange = (e) => {
      const opts = ['preferred', 'include'];
      const optsToSetValue = remove(opts, (opt) => opt === e.target.value);
      const optToSetValue = defaultTo(optsToSetValue[0], 'preferred');
      const optToSetUndefined = defaultTo(opts[0], 'include');
      onChange(optToSetValue, 'gender', getGenderValue(gender));
      onChange(optToSetUndefined, 'gender', undefined);
    };

    const ageGroupsHint = getPmax2RtbAgeGroupsByAgeRange(ageMin, ageMax)
      .map((group) => group.label)
      .join(',');

    return (
      <div>
        <Form.Group as={Row} controlId={'age'}>
          <Form.Label>{i18n.t('limitation.labels.age')}</Form.Label>
          <div>
            <div className={styles.ageSelector}>
              <Select
                className={styles.ageMin}
                options={ageMinOptions}
                name='ageMin'
                simpleValue
                value={ageMin}
                onChange={onAgeMinChange}
              />
              ~
              <Select
                className={styles.ageMax}
                options={ageMaxOptions}
                name='ageMax'
                simpleValue
                value={ageMax}
                onChange={onAgeMaxChange}
              />
            </div>
            <span className={styles.ageGroupsHint}>
              {i18n.t('limitation.hints.ageGroups', {
                groups: ageGroupsHint
              })}
            </span>
          </div>
        </Form.Group>
        <Fragment>
          <Form.Group
            as={Row}
            controlId={'age-opt-preferred'}
            style={{ marginBottom: 0 }}
          >
            <Form.Label>{''}</Form.Label>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t('rtbCampaign.limitation.operationSet.preferred')}
                value={'preferred'}
                id={'age_preferred'}
                onChange={onAgeOptChange}
                checked={ageOptValue === 'preferred'}
                disabled={false}
              />
            </div>
          </Form.Group>
          <Form.Group as={Row} controlId={'age-opt-include'}>
            <Form.Label>{''}</Form.Label>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t('rtbCampaign.limitation.operationSet.include')}
                value={'include'}
                id={'age_include'}
                onChange={onAgeOptChange}
                checked={ageOptValue === 'include'}
                disabled={false}
              />
            </div>
          </Form.Group>
        </Fragment>
        <Form.Group as={Row} controlId={'gender'}>
          <Form.Label>{i18n.t('limitation.labels.gender')}</Form.Label>
          <Select
            options={getGenderOptions()}
            value={gender}
            onChange={onGendersChange}
          />
        </Form.Group>
        {gender.value !== -1 && (
          <Fragment>
            <Form.Group
              as={Row}
              controlId={'gender-opt-preferred'}
              style={{ marginBottom: 0 }}
            >
              <Form.Label>{''}</Form.Label>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t(
                    'rtbCampaign.limitation.operationSet.preferred'
                  )}
                  value={'preferred'}
                  id={'gender_preferred'}
                  onChange={onGenderOptChange}
                  checked={genderOptValue === 'preferred'}
                  disabled={false}
                />
              </div>
            </Form.Group>
            <Form.Group as={Row} controlId={'gender-opt-include'}>
              <Form.Label>{''}</Form.Label>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t('rtbCampaign.limitation.operationSet.include')}
                  value={'include'}
                  id={'gender_include'}
                  onChange={onGenderOptChange}
                  checked={genderOptValue === 'include'}
                  disabled={false}
                />
              </div>
            </Form.Group>
          </Fragment>
        )}
      </div>
    );
  };

  handleScrollToOperate = (operate: string) => {
    document
      .querySelector(`[data-operate="${operate}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  validateImportOutdoorDeviceFile = (file: File): string | undefined => {
    const isCsv =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLowerCase().endsWith('.csv');
    if (!isCsv) {
      return i18n.t('goSegmentForm.errors.uploadTypeErrorHint', {
        type: file.type || 'unknown'
      });
    }
    if (file.size > IMPORT_OUTDOOR_DEVICE_MAX_SIZE) {
      return i18n.t('creativeSetupFlow.labels.storageErrorHint', {
        storage1: formatBytes(file.size),
        storage2: '1MB'
      });
    }
    return undefined;
  };

  handleImportOutdoorDeliveryTargets = ({ file }: { file?: File }) => {
    if (!file) {
      this.setState({ importError: undefined });
      return;
    }
    const importError = this.validateImportOutdoorDeviceFile(file);
    this.setState({ importError });
    if (importError) {
      return;
    }
    this.props.model.importOutdoorDeliveryTargets(file);
    this.handleScrollToOperate(OPERATE.INCLUDE);
  };

  renderFixOutdoorLimitationSection = (limitationType?: string) => {
    const { importError } = this.state;
    const isStore = limitationType === 'storeNum';
    const switchLabel = i18n.t(
      isStore
        ? 'rtbCampaignSetupStep.labels.fixOutdoorStoreSection'
        : 'rtbCampaignSetupStep.labels.fixOutdoorDeviceSection'
    );
    const switchHint = i18n.t(
      isStore
        ? 'rtbCampaignSetupStep.hints.fixOutdoorStoreSection'
        : 'rtbCampaignSetupStep.hints.fixOutdoorDeviceSection'
    );
    const inputLabel = i18n.t(
      isStore
        ? 'campaign.buttons.importOutdoorStore'
        : 'campaign.buttons.importOutdoorDevice'
    );

    return (
      <>
        <FormikField.Switch
          label={switchLabel}
          name='fixOutdoorSpace'
          formGroupClassName={styles.fixOutdoorSpaceSection}
          hint={switchHint}
        />
        <FileInputWithFieldLayout
          name='importOutdoorDeliveryTarget'
          type='other'
          label={inputLabel}
          className={styles.importUploadArea}
          validTypes={['text/csv', 'application/vnd.ms-excel']}
          icon={faFileCsv}
          value={undefined}
          errorBorderHint={!!importError}
          error={importError}
          fieldContentWidth='auto'
          onChange={this.handleImportOutdoorDeliveryTargets}
          hint={
            <>
              {i18n.t('goSegmentForm.hints.fileType')}
              <br />
              {i18n.t('creativeSetupFlow.labels.storageHint', {
                storage: '1MB'
              })}
            </>
          }
          permanentHint={
            <span className={styles.hint}>
              <Trans
                i18nKey='outdoorForm.hints.selfCheckFileExample'
                components={{
                  1: (
                    <a
                      href={`https://storage.googleapis.com/${config.cloudStorageConfig.bucket}/${isStore ? 'importStoreExample' : 'importDeviceExample'}.csv`}
                      download
                    >
                      Click here
                    </a>
                  )
                }}
              />
            </span>
          }
        />
      </>
    );
  };

  renderWeatherSection = () => {
    const limitationModel = this.props.model.getLimitationModel();
    return <WeatherLimitationSection limitationModel={limitationModel} />;
  };

  renderMapSection = (
    handleChange: (
      operate: string,
      type: string,
      value: SelectOptions[]
    ) => void
  ) => {
    const basicFormModel =
      this.props.model.getRtbCampaignBasicFormModel() as AbstractOutdoorLimitationCampaignFormModel;
    const {
      spaceAttributes,
      outdoorChannelId: spaceChannelId,
      fetchSpaceAndUpdateMaps: fetchMaps,
      mapsApiKey,
      mapDeviceType,
      state: { spaceAttributeMap, deviceMap, spaceDetailMap }
    } = basicFormModel;
    const { limitationValue } = this.props.model.getLimitationModel();
    const deviceUnionFind =
      basicFormModel instanceof AbstractOutdoorSynchronizedPlaybackFormModel
        ? basicFormModel.deviceUnionFind
        : undefined;
    return (
      <OutdoorMapSection
        limitationValue={limitationValue}
        onLimitationChange={handleChange}
        spaceAttributes={spaceAttributes}
        spaceAttributeMap={spaceAttributeMap}
        deviceMap={deviceMap}
        spaceDetailMap={spaceDetailMap}
        spaceChannelId={spaceChannelId}
        fetchMaps={fetchMaps}
        mapsApiKey={mapsApiKey}
        mapDeviceType={mapDeviceType}
        deviceUnionFind={deviceUnionFind}
        overBindingLimitValues={basicFormModel.overBindingLimitOptionValues}
      />
    );
  };

  renderFormContent = (formikProps) => {
    const model = this.props.model;
    const order = model.flowModel.order;
    const l1Object = model.flowModel.l1Object;
    const basicFormModel = model.getRtbCampaignBasicFormModel();
    const limitationModel = model.getLimitationModel();
    const isOutdoorLimitationCampaign =
      basicFormModel instanceof AbstractOutdoorLimitationCampaignFormModel;
    if (isOutdoorLimitationCampaign) {
      basicFormModel.setLimitationSettingResetHook(
        limitationModel.setLimitationSetting
      );
    }
    const handleLimitationChange = (limitationValue) => {
      if (isOutdoorLimitationCampaign) {
        const testModeLimitation = limitationValue?.other?.find(
          (item: any) => item.type === 'testMode'
        );
        if (testModeLimitation !== undefined) {
          const isTestMode = testModeLimitation.value === 1;
          this.props.model.setTestMode(isTestMode);
        }
      }
    };
    model.setFormikProps(formikProps);
    const onTabSelect = (tab) => {
      if (tab === null) {
        return;
      }
      model.goSubStep(+tab);
    };
    const onSaveDraft = model.flowModel.onSaveDraft;
    const fixOutdoorSpace = formikProps.values.fixOutdoorSpace;
    // 把 fixOutdoorSpace 的即時值同步進 outdoor model，讓固定門市 chip 的健康狀態
    // 在使用者即時切換開關時就改用單台 spaceId 狀態（campaignBasic 只有載入時的值）。
    if (isOutdoorLimitationCampaign) {
      basicFormModel.setLiveFixOutdoorSpace(!!fixOutdoorSpace);
      // 電子紙的機台名額是逐日判定的，走期一改能選的機台就跟著變（ECOPD-5615）。
      // 與 fixOutdoorSpace 同一條路推入即時值；setLiveDateRange 自己比對有沒有變，
      // 沒變就什麼都不做，所以在 render 途中呼叫是安全的。
      basicFormModel.setLiveDateRange(
        formikProps.values.startDate,
        formikProps.values.endDate
      );
    }
    const supportFixOutdoorSpace =
      isOutdoorLimitationCampaign &&
      !basicFormModel.hideFixOutdoorSpace &&
      basicFormModel.adTypeSetting?.screenDisplayType !==
        ScreenDisplayType.SYNCHRONIZED_PLAYBACK;
    const limitationType = isOutdoorLimitationCampaign
      ? basicFormModel.mapDeviceType
      : undefined;
    const isMovableOutdoorLimitationCampaign =
      basicFormModel instanceof AbstractDynamicMovableOutdoorFormModel;
    const supportWeatherTargeting =
      (isOutdoorLimitationCampaign || isMovableOutdoorLimitationCampaign) &&
      !fixOutdoorSpace &&
      get(
        ChannelGroupSetting.fromId(l1Object.channelGroupId).config,
        'addonFeatures.campaign.supportWeatherTargeting',
        false
      ) &&
      basicFormModel.adTypeSetting?.screenDisplayType !==
        ScreenDisplayType.SYNCHRONIZED_PLAYBACK;
    const supportMapSection = isOutdoorLimitationCampaign;
    return (
      <>
        <Tabs
          id='campaignFromTab'
          activeKey={model.activeTab}
          onSelect={onTabSelect}
        >
          <Tab
            eventKey={CampaignSetupTab.BASIC}
            title={i18n.t('stepSideBar.labels.basic')}
          >
            <CampaignBasicForm model={basicFormModel!} />
          </Tab>
          <Tab
            eventKey={CampaignSetupTab.LIMITATION}
            title={i18n.t('stepSideBar.labels.targeting')}
          >
            <EditLimitation
              model={limitationModel}
              order={order}
              onChange={handleLimitationChange}
              renderGeneralFields={
                basicFormModel?.limitationConfig.supportAgeGenderLimitation
                  ? this.renderAgeAndGender
                  : undefined
              }
              renderFixOutdoorLimitationSection={
                supportFixOutdoorSpace
                  ? partial(
                      this.renderFixOutdoorLimitationSection,
                      limitationType
                    )
                  : undefined
              }
              renderWeatherSection={
                supportWeatherTargeting ? this.renderWeatherSection : undefined
              }
              renderMapSection={
                supportMapSection ? this.renderMapSection : undefined
              }
              renderGeocoderBinder={
                isMovableOutdoorLimitationCampaign
                  ? () => (
                      <MovableOutdoorGeocoderBinder
                        formModel={basicFormModel}
                      />
                    )
                  : undefined
              }
            />
          </Tab>
          {model.flowModel.needSetupTracking && basicFormModel && (
            <Tab
              eventKey={CampaignSetupTab.TRACKING}
              title={i18n.t('stepSideBar.labels.strategyAndTracking')}
            >
              <CampaignTrackForm
                trackingConfig={basicFormModel.trackingConfig}
              />
            </Tab>
          )}
        </Tabs>
        <div className={styles.buttonArea}>
          <Button variant='primary' size='sm' onClick={model.goNext}>
            {i18n.t('campaign.buttons.completeAndCheck')}
          </Button>
          {onSaveDraft && (
            <Button
              variant='secondary'
              size='sm'
              onClick={partial(onSaveDraft, {
                basic: formikProps.values,
                limitations: limitationModel
                  ? limitationModel.limitationValue
                  : {
                      include: [],
                      exclude: [],
                      preferred: [],
                      nonPreferred: [],
                      other: []
                    }
              })}
            >
              {i18n.t('campaign.buttons.saveDraft')}
            </Button>
          )}
          {model.activeTab === CampaignSetupTab.LIMITATION &&
            fixOutdoorSpace && (
              <Button
                variant='secondary'
                size='sm'
                onClick={model.downloadFixOutdoorLimitationData}
              >
                {i18n.t(
                  limitationType === 'storeNum'
                    ? 'campaign.buttons.downloadFixOutdoorStoreData'
                    : 'campaign.buttons.downloadFixOutdoorDeviceData'
                )}
              </Button>
            )}
          {model.flowModel.type === 'create' && (
            <Button variant='secondary' size='sm' onClick={model.goLast}>
              {i18n.t('campaign.buttons.back')}
            </Button>
          )}
        </div>
      </>
    );
  };

  render() {
    const model = this.props.model;
    const campaignData = model.flowModel.state.campaign;
    return (
      <div className={styles.setupStep}>
        <Formik
          initialValues={campaignData!.basic}
          enableReinitialize
          onSubmit={this.handleSubmit}
          validate={this.validate}
          validateOnBlur={false}
        >
          {this.renderFormContent}
        </Formik>
      </div>
    );
  }
}
